import { useEffect, useCallback, useState } from 'react';

// Global keyboard shortcut hook
export const useKeyboardShortcuts = (shortcuts = {}) => {
  useEffect(() => {
    const handler = (e) => {
      // Don't trigger when typing in inputs (unless it's Escape or Ctrl combos)
      const tag = e.target.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable;

      if (e.key === 'Escape' && shortcuts.onEscape) {
        e.preventDefault();
        shortcuts.onEscape();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 's' && shortcuts.onSave) {
        e.preventDefault();
        shortcuts.onSave();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && shortcuts.onNew) {
        e.preventDefault();
        shortcuts.onNew();
        return;
      }

      if (e.key === '?' && !isInput && shortcuts.onHelp) {
        e.preventDefault();
        shortcuts.onHelp();
        return;
      }

      // Arrow/Enter only when not in text inputs
      if (isInput && !['Escape'].includes(e.key)) return;

      if (e.key === 'ArrowDown' && shortcuts.onDown) { e.preventDefault(); shortcuts.onDown(); }
      if (e.key === 'ArrowUp' && shortcuts.onUp) { e.preventDefault(); shortcuts.onUp(); }
      if (e.key === 'ArrowLeft' && shortcuts.onLeft) { e.preventDefault(); shortcuts.onLeft(); }
      if (e.key === 'ArrowRight' && shortcuts.onRight) { e.preventDefault(); shortcuts.onRight(); }
      if (e.key === 'Enter' && shortcuts.onEnter) { e.preventDefault(); shortcuts.onEnter(); }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shortcuts]);
};

// Grid navigation hook for tables/variant grids
export const useGridNavigation = (rows, cols) => {
  const [activeCell, setActiveCell] = useState({ row: 0, col: 0 });

  const navigate = useCallback((dir) => {
    setActiveCell(prev => {
      let { row, col } = prev;
      if (dir === 'up') row = Math.max(0, row - 1);
      if (dir === 'down') row = Math.min(rows - 1, row + 1);
      if (dir === 'left') col = Math.max(0, col - 1);
      if (dir === 'right') col = Math.min(cols - 1, col + 1);
      return { row, col };
    });
  }, [rows, cols]);

  return { activeCell, setActiveCell, navigate };
};

// Shortcut help overlay component
export const ShortcutHelpOverlay = ({ open, onClose }) => {
  if (!open) return null;

  const shortcuts = [
    { key: 'Ctrl + S', desc: 'Save current form' },
    { key: 'Ctrl + N', desc: 'Create new item' },
    { key: 'Esc', desc: 'Close dialog / Cancel' },
    { key: '?', desc: 'Show this help' },
    { key: 'Arrow Keys', desc: 'Navigate grid cells' },
    { key: 'Enter', desc: 'Confirm / Select item' },
    { key: 'Tab', desc: 'Move to next field' },
    { key: 'Shift + Tab', desc: 'Move to previous field' },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center" onClick={onClose} data-testid="shortcut-help-overlay">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 animate-in fade-in zoom-in-95" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Keyboard Shortcuts</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="space-y-2">
          {shortcuts.map(s => (
            <div key={s.key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors">
              <span className="text-sm text-gray-600">{s.desc}</span>
              <kbd className="bg-gray-100 border border-gray-300 text-gray-700 px-2.5 py-1 rounded-md text-xs font-mono font-semibold shadow-sm">{s.key}</kbd>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-4 text-center">Press Esc or click outside to close</p>
      </div>
    </div>
  );
};
